import {
  PaginatedResponse,
  PaginatedRequestParams,
  RtkEmptySplitApi,
} from "../../types";
import {
  CreateInvoicePayload,
  CreateInvoicesConfigPayload,
  UpdateInvoicePayload,
  UpdateInvoicesConfigPayload,
} from "./types";
import { InvoiceModel, InvoicesConfigModel, InvoiceType } from "./models";
import {
  getInvoiceIdTag,
  getInvoiceListTag,
  getInvoiceConfigTag,
  getInvoiceConfigListTag,
  INVOICE_TAG_TYPES,
} from "./utils";
import { GetInvoicesListParams } from "./types/GetInvoicesListParams";

const normalizeIsoString = (isoString: string) =>
  isoString ? `${isoString?.split("T")[0]}T00:00:00` : isoString;

export const addInvoicesApi = ({ enhanceEndpoints }: RtkEmptySplitApi) => {
  const apiWithTags = enhanceEndpoints({
    addTagTypes: [
      INVOICE_TAG_TYPES.INVOICE,
      INVOICE_TAG_TYPES.INVOICE_CONFIG,
      INVOICE_TAG_TYPES.INVOICE_LIST,
    ],
  });

  return apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: ({ query, mutation }) => ({
      invoicesList: query<
        PaginatedResponse<InvoiceModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<GetInvoicesListParams>;
          silentError?: boolean;
        }
      >({
        query: ({ entityId, params, silentError }) => ({
          url: `/api/auth/${entityId}/invoice`,
          params,
          silentError,
        }),
        providesTags: (_result, _error, { entityId }) =>
          getInvoiceListTag(entityId),
        transformResponse: (response: PaginatedResponse<InvoiceModel>) => ({
          ...response,
          data: response.data.map((item) => ({
            ...item,
            due_on: normalizeIsoString(item.due_on),
            date: normalizeIsoString(item.date),
          })),
        }),
      }),

      guestInvoicesList: query<
        PaginatedResponse<InvoiceModel>,
        {
          entityId: string;
          params: PaginatedRequestParams<GetInvoicesListParams>;
          silentError?: boolean;
          isGuest?: boolean;
        }
      >({
        query: ({ entityId, params, silentError, isGuest = false }) => ({
          url: isGuest
            ? `/api/entity/${entityId}/guest/task/invoice`
            : `/api/auth/entity/${entityId}/guest/task/invoice`,
          params,
          silentError,
        }),
        providesTags: (_result, _error, { entityId }) =>
          getInvoiceListTag(entityId),
        transformResponse: (response: PaginatedResponse<InvoiceModel>) => ({
          ...response,
          data: response.data.map((item) => ({
            ...item,
            due_on: normalizeIsoString(item.due_on),
            date: normalizeIsoString(item.date),
          })),
        }),
      }),

      createInvoice: mutation<
        InvoiceModel,
        {
          entityId: string;
          payload: CreateInvoicePayload;
        }
      >({
        query: ({ entityId, payload }) => ({
          url: `/api/auth/${entityId}/invoice`,
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          data: payload,
        }),
        invalidatesTags: (_result, _error, { entityId }) =>
          getInvoiceListTag(entityId),
      }),

      updateInvoice: mutation<
        InvoiceModel,
        {
          entityId: string;
          invoiceId: string;
          payload: Partial<UpdateInvoicePayload>;
        }
      >({
        query: ({ entityId, invoiceId, payload }) => {
          const formData = new FormData();
          formData.append("invoice_config", JSON.stringify(payload));

          return {
            url: `/api/auth/${entityId}/invoice/${invoiceId}`,
            method: "PUT",
            data: payload,
          };
        },
        invalidatesTags: (_result, _error, { entityId, invoiceId }) => [
          ...getInvoiceListTag(entityId),
          ...getInvoiceIdTag(invoiceId),
        ],
      }),

      getInvoice: query<
        InvoiceModel,
        {
          entityId: string;
          invoiceId: string;
        }
      >({
        query: ({ entityId, invoiceId }) => ({
          url: `/api/auth/${entityId}/invoice/${invoiceId}`,
        }),
        transformResponse: (response: InvoiceModel) => ({
          ...response,
          due_on: normalizeIsoString(response.due_on),
          date: normalizeIsoString(response.date),
        }),
        providesTags: (_result, _error, { invoiceId }) =>
          getInvoiceIdTag(invoiceId),
      }),

      deleteInvoice: mutation<
        void,
        {
          entityId: string;
          invoiceId: string;
        }
      >({
        query: ({ entityId, invoiceId }) => ({
          url: `/api/auth/${entityId}/invoice/${invoiceId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { entityId }) =>
          getInvoiceListTag(entityId),
      }),

      bulkDeleteInvoices: mutation<
        void,
        {
          entityId: string;
          ids: string[];
        }
      >({
        query: ({ entityId, ids }) => ({
          url: `/api/auth/${entityId}/invoice/bulk`,
          method: "DELETE",
          params: {
            ids,
          },
        }),
        invalidatesTags: (_result, _error, { entityId }) =>
          getInvoiceListTag(entityId),
      }),

      getConfigList: query<
        PaginatedResponse<InvoicesConfigModel>,
        {
          entityId: string;
        }
      >({
        query: ({ entityId }) => ({
          url: `/api/auth/${entityId}/invoice/config?page=1&page_size=10`,
        }),
        providesTags: (_result, _error, { entityId }) =>
          getInvoiceConfigListTag(entityId),
      }),

      getConfig: query<
        { data: InvoicesConfigModel },
        {
          entityId: string;
          configId: string;
        }
      >({
        query: ({ entityId, configId }) => ({
          url: `/api/auth/${entityId}/invoice/config/${configId}`,
        }),
        providesTags: (_result, _error, { configId }) =>
          getInvoiceConfigTag(configId),
      }),

      createConfig: mutation<
        InvoiceModel,
        {
          entityId: string;
          payload: CreateInvoicesConfigPayload;
          logo: File | undefined;
        }
      >({
        query: ({ entityId, payload, logo }) => {
          const formData = new FormData();

          formData.append("invoice_config", JSON.stringify(payload));

          if (logo) {
            formData.append("logo_file", logo);
          }

          return {
            url: `/api/auth/${entityId}/invoice/config`,
            headers: {
              "Content-Type": "multipart/form-data",
            },
            method: "POST",
            data: formData,
          };
        },
        invalidatesTags: (_result, _error, { entityId }) =>
          getInvoiceConfigListTag(entityId),
      }),

      updateConfig: mutation<
        InvoicesConfigModel,
        {
          entityId: string;
          configId: string;
          payload: UpdateInvoicesConfigPayload;
          logo: File | undefined | null;
        }
      >({
        query: ({ entityId, configId, payload, logo }) => {
          const formData = new FormData();

          formData.append("invoice_config", JSON.stringify(payload));

          if (logo !== undefined) {
            formData.append("logo_file", logo ?? "");
          }

          return {
            url: `/api/auth/${entityId}/invoice/config/${configId}`,
            headers: {
              "Content-Type": "multipart/form-data",
            },
            method: "PUT",
            data: formData,
          };
        },
        invalidatesTags: (_result, _error, { entityId, configId }) => [
          ...getInvoiceConfigListTag(entityId),
          ...getInvoiceConfigTag(configId),
        ],
      }),

      getInvoiceByShortId: query<
        InvoiceModel & {
          entity: {
            name: string;
            banner_img: string | null;
            display_pic: string | null;
          };
        },
        {
          shortId: string;
        }
      >({
        query: ({ shortId }) => ({
          url: `/I/${shortId}`,
        }),
        transformResponse: (response: {
          module: string;
          module_res: InvoiceModel & {
            entity: {
              name: string;
              banner_img: string | null;
              display_pic: string | null;
            };
          };
        }) => ({
          ...response.module_res,
          due_on: normalizeIsoString(response.module_res.due_on),
          date: normalizeIsoString(response.module_res.date),
        }),
        providesTags: (_result, _error, { shortId }) => [
          { type: INVOICE_TAG_TYPES.INVOICE, id: shortId },
        ],
      }),

      createInvoiceFromExisting: mutation<
        InvoiceModel,
        {
          entityId: string;
          existingInvoiceId: string;
          payload: { type: InvoiceType };
        }
      >({
        query: ({ entityId, existingInvoiceId, payload }) => ({
          url: `/api/auth/${entityId}/invoice/existing/${existingInvoiceId}`,
          method: "POST",
          data: payload,
        }),
        invalidatesTags: (_result, _error, { entityId }) =>
          getInvoiceListTag(entityId),
      }),

      deleteConfigLogo: mutation<
        void,
        {
          entityId: string;
          configId: string;
        }
      >({
        query: ({ entityId, configId }) => ({
          url: `/api/auth/${entityId}/invoice/config/logo/${configId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_result, _error, { entityId, configId }) => [
          ...getInvoiceConfigListTag(entityId),
          ...getInvoiceConfigTag(configId),
        ],
      }),
    }),
  });
};

export type InvoicesApi = ReturnType<typeof addInvoicesApi>;
